import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { Loader2, Package, DollarSign, ArrowLeft, Layers } from 'lucide-react';
import {
  LANDED_COST_FIELD_KEYS,
  type LandedCostKey,
  type LandedCostRow,
  type DistributionMethod,
  newLandedCostRow,
  getAllocationPct,
  getEffectiveAmount,
  getVolumeAllocationIssues,
  LandedCostsTable,
} from './_landed-costs';

interface StoredLandedCostDetail {
  key: LandedCostKey;
  originalCurrency: string;
  originalAmount: number;
  fxRate: number;
  fxRateSource: string;
  amountTRY: number;
  kdvRate: number;
  kdvAmountOriginal: number;
  kdvAmountTRY: number;
  isKdvOnly: boolean;
  method: DistributionMethod;
  allocationPct: number;
  sellerId: string | null;
}

interface BatchReceipt {
  id: string;
  receiptNumber: string;
  purchaseOrderId: string;
  purchaseOrderNumber: string;
  currency: string;
  warehouseName: string;
  itemCount: number;
  itemsSubtotal: number;
  totalLandedCost: number;
  landedCosts: Record<string, number>;
  landedCostDetails: StoredLandedCostDetail[] | null;
}

/**
 * Rebuild the editable landedCosts state from saved per-GRN detail slices.
 * For a multi-PO batch, details from all sibling GRNs are grouped by cost
 * type: the originalAmount slices are summed back into the pre-split total
 * and each sibling's allocationPct becomes the manualAllocations entry for
 * that PO — giving us the exact state the user had at creation time.
 */
function rebuildLandedCostsFromBatch(
  siblings: Pick<BatchReceipt, 'id' | 'purchaseOrderId' | 'landedCostDetails'>[]
): LandedCostRow[] {
  const byKey = new Map<
    LandedCostKey,
    { rows: Array<StoredLandedCostDetail & { poId: string }>; first: StoredLandedCostDetail }
  >();

  for (const sibling of siblings) {
    for (const d of sibling.landedCostDetails ?? []) {
      const entry = byKey.get(d.key);
      const withPo = { ...d, poId: sibling.purchaseOrderId };
      if (entry) {
        entry.rows.push(withPo);
      } else {
        byKey.set(d.key, { rows: [withPo], first: d });
      }
    }
  }

  const rebuilt: LandedCostRow[] = [];
  for (const [key, { rows, first }] of byKey) {
    const totalOriginal = rows.reduce((s, r) => s + r.originalAmount, 0);
    const totalKdvOriginal = rows.reduce((s, r) => s + r.kdvAmountOriginal, 0);
    const manualAllocations: Record<string, number> = {};
    for (const r of rows) {
      manualAllocations[r.poId] = r.allocationPct;
    }

    rebuilt.push({
      key,
      currency: first.originalCurrency,
      amount: first.isKdvOnly ? 0 : totalOriginal,
      kdvRate: first.kdvRate,
      isKdvOnly: first.isKdvOnly,
      amountWithKdv: first.isKdvOnly
        ? totalKdvOriginal
        : first.kdvRate > 0
          ? Math.round(totalOriginal * (1 + first.kdvRate / 100) * 100) / 100
          : 0,
      method: first.method,
      manualAllocations,
      sellerId: first.sellerId,
    });
  }

  // Keep a stable ordering based on LANDED_COST_FIELD_KEYS so the rows
  // appear in the same sequence every time.
  rebuilt.sort(
    (a, b) => LANDED_COST_FIELD_KEYS.indexOf(a.key) - LANDED_COST_FIELD_KEYS.indexOf(b.key)
  );

  // Append any pre-filled empty rows so the user can still add previously
  // unused cost types without having to open the "add" popover first.
  const usedKeys = new Set(rebuilt.map((r) => r.key));
  for (const key of LANDED_COST_FIELD_KEYS) {
    if (!usedKeys.has(key)) rebuilt.push(newLandedCostRow(key));
  }

  return rebuilt;
}

export function EditLandedCostsPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const { data: receipt, isLoading: loadingReceipt } = trpc.tradingGoodsReceipt.byId.useQuery(id!, {
    enabled: !!id,
  });
  const { data: batch, isLoading: loadingBatch } = trpc.tradingGoodsReceipt.byBatchId.useQuery(
    receipt?.receiptBatchId ?? '',
    { enabled: !!receipt?.receiptBatchId }
  );
  const { data: sellersData } = trpc.tradingCari.list.useQuery({ cariType: 'S', pageSize: 200 });

  // Derived: list of all sibling GRNs (single element when not part of a batch).
  const siblings = useMemo<BatchReceipt[]>(() => {
    if (batch?.receipts?.length) {
      return batch.receipts as unknown as BatchReceipt[];
    }
    if (receipt) {
      return [
        {
          id: receipt.id,
          receiptNumber: receipt.receiptNumber,
          purchaseOrderId: receipt.purchaseOrderId,
          purchaseOrderNumber: receipt.purchaseOrder.orderNumber,
          currency: receipt.purchaseOrder.currency,
          warehouseName: receipt.warehouse.name,
          itemCount: receipt.items.length,
          itemsSubtotal: receipt.itemsSubtotal ?? 0,
          totalLandedCost: receipt.totalLandedCost ?? 0,
          landedCosts: Object.fromEntries(
            LANDED_COST_FIELD_KEYS.map((f) => [f, (receipt as any)[f] ?? 0])
          ),
          landedCostDetails:
            (receipt.landedCostDetails as unknown as StoredLandedCostDetail[] | null) ?? null,
        },
      ];
    }
    return [];
  }, [batch, receipt]);

  const [landedCosts, setLandedCosts] = useState<LandedCostRow[]>([]);
  const [supplierInvoice, setSupplierInvoice] = useState('');
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Rebuild editable state once all sibling data is loaded
  useEffect(() => {
    if (siblings.length > 0) {
      setLandedCosts(rebuildLandedCostsFromBatch(siblings));
    }
  }, [siblings]);

  useEffect(() => {
    if (receipt) {
      setSupplierInvoice(receipt.supplierInvoice ?? '');
      setNotes(receipt.notes ?? '');
    }
  }, [receipt]);

  // --- Derived totals for the <LandedCostsTable /> ---
  // Each sibling contributes its own itemsSubtotal / item quantity to the
  // distribution math. We read these directly from the batch response so
  // we don't need to re-fetch per-item detail.
  const { poSubtotals, poQuantities } = useMemo(() => {
    const subs: Record<string, number> = {};
    const qtys: Record<string, number> = {};
    for (const s of siblings) {
      subs[s.purchaseOrderId] = s.itemsSubtotal ?? 0;
      qtys[s.purchaseOrderId] = s.itemCount ?? 0;
    }
    return { poSubtotals: subs, poQuantities: qtys };
  }, [siblings]);

  const pos = useMemo(
    () =>
      siblings.map((s) => ({
        id: s.purchaseOrderId,
        orderNumber: s.purchaseOrderNumber,
        supplierName: '',
        invoiceNumber: null,
        currency: s.currency,
      })),
    [siblings]
  );

  const updateMutation = trpc.tradingGoodsReceipt.updateLandedCostsBatch.useMutation();

  const onSave = async () => {
    if (siblings.length === 0) return;

    // Same guard as the create page: a 'volume' cost row whose manual per-PO
    // percentages don't total 100% would silently mis-inventory the cost.
    const volumeIssues = getVolumeAllocationIssues(
      landedCosts,
      siblings.map((s) => s.purchaseOrderId)
    );
    if (volumeIssues.length > 0) {
      showErrorToast(
        t('purchasing.goodsReceipts.volumeAllocationError', {
          rows: volumeIssues
            .map((i) => `${t(`purchasing.goodsReceipts.lc_${i.key}`)} (${i.sum.toFixed(0)}%)`)
            .join(', '),
        })
      );
      return;
    }

    setIsSaving(true);
    try {
      // Slice the batch-level rows back into per-GRN detail payloads —
      // mirrors the submit logic of the create page so the backend sees
      // the same shape whether we're creating or editing.
      const receiptsPayload = siblings.map((sib) => {
        const details = landedCosts
          .filter((c) => (c.isKdvOnly ? c.amountWithKdv > 0 : c.amount > 0))
          .map((c) => {
            const pct = getAllocationPct(c, sib.purchaseOrderId, poSubtotals, poQuantities);
            const originalAmount = c.isKdvOnly
              ? 0
              : Math.round(((c.amount * pct) / 100) * 100) / 100;
            const fullKdvOriginal = c.isKdvOnly
              ? c.amountWithKdv
              : c.kdvRate > 0
                ? Math.round(((c.amount * c.kdvRate) / 100) * 100) / 100
                : 0;
            const kdvAmountOriginal = Math.round(((fullKdvOriginal * pct) / 100) * 100) / 100;
            return {
              key: c.key,
              originalCurrency: c.currency,
              originalAmount,
              kdvRate: c.kdvRate,
              kdvAmountOriginal,
              isKdvOnly: c.isKdvOnly,
              method: c.method,
              allocationPct: pct,
              sellerId: c.sellerId,
            };
          })
          .filter((d) => d.originalAmount > 0 || d.kdvAmountOriginal > 0);
        return {
          id: sib.id,
          landedCostDetails: details,
          // Only the focused receipt's notes/invoice can be edited from this
          // page. Other siblings keep their existing values.
          supplierInvoice: sib.id === id ? supplierInvoice || null : undefined,
          notes: sib.id === id ? notes || null : undefined,
        };
      });

      await updateMutation.mutateAsync({ receipts: receiptsPayload });
      showSuccessToast(t('purchasing.goodsReceipts.updatedSuccess'));
      utils.tradingGoodsReceipt.byId.invalidate(id);
      if (receipt?.receiptBatchId) {
        utils.tradingGoodsReceipt.byBatchId.invalidate(receipt.receiptBatchId);
      }
      utils.tradingGoodsReceipt.list.invalidate();
      utils.stockLevel.list.invalidate();
      utils.stockLevel.getProductBatches.invalidate();
      navigate(`/trading/purchasing/receipts/${id}`);
    } catch (err: any) {
      showErrorToast(err.message || 'Update failed');
    } finally {
      setIsSaving(false);
    }
  };

  if (loadingReceipt || (receipt?.receiptBatchId && loadingBatch)) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!receipt) {
    return <div className="text-muted-foreground p-4 text-sm">Goods receipt not found.</div>;
  }

  // Grand totals for summary card — sum the effective (base) amount per currency.
  const landedByCurrency: Record<string, number> = {};
  for (const c of landedCosts) {
    const amt = getEffectiveAmount(c);
    if (amt > 0) landedByCurrency[c.currency] = (landedByCurrency[c.currency] ?? 0) + amt;
  }
  const itemsByCurrency: Record<string, number> = {};
  for (const s of siblings) {
    if ((s.itemsSubtotal ?? 0) > 0) {
      itemsByCurrency[s.currency] = (itemsByCurrency[s.currency] ?? 0) + s.itemsSubtotal;
    }
  }
  const grandByCurrency: Record<string, number> = { ...itemsByCurrency };
  for (const [cur, amt] of Object.entries(landedByCurrency)) {
    grandByCurrency[cur] = (grandByCurrency[cur] ?? 0) + amt;
  }
  const formatMultiCurrency = (totals: Record<string, number>) =>
    Object.entries(totals)
      .filter(([, amt]) => amt > 0)
      .map(([cur, amt]) => formatCurrency(amt, cur))
      .join(' + ');

  const isBatch = siblings.length > 1;

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/trading/purchasing/receipts/${id}`)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{t('purchasing.goodsReceipts.editLandedCosts')}</h1>
            <p className="text-muted-foreground text-sm">{receipt.receiptNumber}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/trading/purchasing/receipts/${id}`)}>
            {tCommon('cancel')}
          </Button>
          <Button onClick={onSave} disabled={isSaving}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {tCommon('save')}
          </Button>
        </div>
      </div>

      {/* Batch context — shown whenever editing a multi-PO receipt */}
      {isBatch && (
        <div className="border-border bg-muted/10 border">
          <div className="bg-muted/40 border-border flex items-center gap-2 border-b px-4 py-2.5">
            <Layers className="text-muted-foreground h-4 w-4" />
            <h2 className="text-sm font-medium">
              {t('purchasing.goodsReceipts.batchTitle')} ({siblings.length})
            </h2>
          </div>
          <div className="text-muted-foreground border-border border-b px-4 py-2 text-xs">
            {t('purchasing.goodsReceipts.editBatchHint')}
          </div>
          <table className="w-full text-xs">
            <thead className="bg-muted/20">
              <tr>
                <th className="px-3 py-1.5 text-left">
                  {t('purchasing.goodsReceipts.editBatchGRN')}
                </th>
                <th className="px-3 py-1.5 text-left">
                  {t('purchasing.goodsReceipts.editBatchPO')}
                </th>
                <th className="px-3 py-1.5 text-right">
                  {t('purchasing.goodsReceipts.editBatchItems')}
                </th>
                <th className="px-3 py-1.5 text-right">
                  {t('purchasing.goodsReceipts.editBatchCurrentLanded')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {siblings.map((s) => (
                <tr key={s.id} className={s.id === id ? 'bg-primary/5' : ''}>
                  <td className="px-3 py-1.5 font-mono">{s.receiptNumber}</td>
                  <td className="px-3 py-1.5 font-mono">{s.purchaseOrderNumber}</td>
                  <td className="px-3 py-1.5 text-right">
                    {formatCurrency(s.itemsSubtotal, s.currency)}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    {formatCurrency(s.totalLandedCost - s.itemsSubtotal, 'TRY')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Supplier invoice + notes (only for the focused receipt) */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label>{t('purchasing.goodsReceipts.supplierInvoice')}</Label>
          <Input
            value={supplierInvoice}
            onChange={(e) => setSupplierInvoice(e.target.value)}
            placeholder="—"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('purchasing.goodsReceipts.notes')}</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder={t('purchasing.goodsReceipts.notesPlaceholder')}
          />
        </div>
      </div>

      {/* Read-only items summary */}
      <div className="border-border bg-card border">
        <div className="bg-muted/20 border-border flex items-center gap-2 border-b px-4 py-2.5">
          <Package className="text-muted-foreground h-4 w-4" />
          <h2 className="text-sm font-medium">{t('purchasing.goodsReceipts.receivedItems')}</h2>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-muted/30">
            <tr>
              <th className="px-4 py-1.5 text-left text-xs font-medium">
                {t('purchasing.goodsReceipts.product')}
              </th>
              <th className="px-4 py-1.5 text-right text-xs font-medium">
                {t('purchasing.goodsReceipts.qty')}
              </th>
              <th className="px-4 py-1.5 text-right text-xs font-medium">
                {t('purchasing.goodsReceipts.editItemsUnitCost')}
              </th>
              <th className="px-4 py-1.5 text-right text-xs font-medium">
                {t('purchasing.goodsReceipts.editItemsTotal')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {receipt.items.map((item) => (
              <tr key={item.id}>
                <td className="px-4 py-1.5">
                  <div className="font-medium">{item.productName}</div>
                  <div className="text-muted-foreground font-mono text-xs">{item.productCode}</div>
                </td>
                <td className="px-4 py-1.5 text-right font-mono">{item.quantity}</td>
                <td className="px-4 py-1.5 text-right font-mono">
                  {formatCurrency(item.unitCost, receipt.purchaseOrder.currency)}
                </td>
                <td className="px-4 py-1.5 text-right font-mono">
                  {formatCurrency(item.quantity * item.unitCost, receipt.purchaseOrder.currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Landed costs table (shared with the create page) */}
      {landedCosts.length > 0 && (
        <LandedCostsTable
          landedCosts={landedCosts}
          setLandedCosts={setLandedCosts}
          pos={pos}
          poSubtotals={poSubtotals}
          poQuantities={poQuantities}
          sellers={sellersData?.items ?? []}
        />
      )}

      {/* Grand total summary */}
      <div className="bg-primary/5 border-primary/20 border p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <DollarSign className="text-primary h-5 w-5" />
              <span className="font-medium">{t('purchasing.goodsReceipts.grandTotal')}</span>
            </div>
            {Object.keys(landedByCurrency).length > 0 && (
              <p className="text-muted-foreground mt-1 text-xs">
                {t('purchasing.goodsReceipts.itemsLabel')}: {formatMultiCurrency(itemsByCurrency)} +{' '}
                {t('purchasing.goodsReceipts.landedCostsTotal')}:{' '}
                {formatMultiCurrency(landedByCurrency)}
              </p>
            )}
          </div>
          <span className="text-xl font-bold">{formatMultiCurrency(grandByCurrency)}</span>
        </div>
      </div>
    </div>
  );
}
